import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Select, FormControl, InputLabel, Alert,
  CircularProgress, LinearProgress, Tooltip, IconButton,
} from '@mui/material';
import {
  PersonAdd as AssignIcon,
  PersonRemove as RevokeIcon,
  CheckCircle as ActiveIcon,
  RadioButtonUnchecked as UnassignedIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { subscriptionService } from '../../services/subscriptionService';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import logger from '../../utils/logger';
import { primaryFamilyCodeFromUserDoc } from '../../utils/familyScope';

const LicenseManagement = () => {
  const { currentUser } = useAuth();
  const familyCode = primaryFamilyCodeFromUserDoc(currentUser);

  const [seatUsage, setSeatUsage] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [assignDialog, setAssignDialog] = useState(null); // { seatId, seatIndex }
  const [selectedMember, setSelectedMember] = useState('');

  const load = useCallback(async () => {
    if (!familyCode) return;
    setLoading(true);
    try {
      const [usage, membersSnap] = await Promise.all([
        subscriptionService.getSeatUsage(familyCode),
        getDocs(query(collection(db, 'users'), where('familyCode', '==', familyCode))),
      ]);

      // Also query by activeFamilyCode and currentFamily for multi-family accounts
      const [byActive, byCurrent] = await Promise.all([
        getDocs(query(collection(db, 'users'), where('activeFamilyCode', '==', familyCode))),
        getDocs(query(collection(db, 'users'), where('currentFamily', '==', familyCode))),
      ]);

      const seen = new Set();
      const allMembers = [];
      for (const snap of [membersSnap, byActive, byCurrent]) {
        snap.docs.forEach(d => {
          if (!seen.has(d.id)) {
            seen.add(d.id);
            allMembers.push({ uid: d.id, ...d.data() });
          }
        });
      }

      setSeatUsage(usage);
      setMembers(allMembers);
    } catch (err) {
      logger.error('LicenseManagement load error:', err);
      setError('Failed to load license data.');
    } finally {
      setLoading(false);
    }
  }, [familyCode]);

  useEffect(() => { load(); }, [load]);

  const memberById = (uid) => members.find(m => m.uid === uid);

  const unassignedMembers = members.filter(
    m => !seatUsage?.seats?.some(s => s.assignedTo === m.uid && s.status === 'active')
  );

  const handleAssign = async () => {
    if (!selectedMember) return;
    setActionLoading(true);
    setError(null);
    try {
      const result = await subscriptionService.assignSeat(familyCode, selectedMember);
      if (result.success) {
        setSuccess('Seat assigned successfully.');
        setAssignDialog(null);
        setSelectedMember('');
        await load();
      } else {
        setError(result.reason || 'Could not assign seat.');
      }
    } catch (err) {
      logger.error('assignSeat error:', err);
      setError(err.message || 'Failed to assign seat.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevoke = async (userId) => {
    setActionLoading(true);
    setError(null);
    try {
      await subscriptionService.revokeSeat(familyCode, userId);
      setSuccess('Seat revoked.');
      await load();
    } catch (err) {
      logger.error('revokeSeat error:', err);
      setError(err.message || 'Failed to revoke seat.');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <Box sx={{ p: 4 }}><CircularProgress /></Box>;
  if (!seatUsage) return <Alert severity="info">No subscription data found. Subscribe to a plan first.</Alert>;

  const { seats = [], totalSeats = 0, usedSeats = 0 } = seatUsage;
  const pct = totalSeats > 0 ? (usedSeats / totalSeats) * 100 : 0;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Summary */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>License Usage</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <strong>{usedSeats} of {totalSeats}</strong> seats active &nbsp;·&nbsp; {totalSeats - usedSeats} available
        </Typography>
        <LinearProgress
          variant="determinate"
          value={pct}
          color={pct >= 100 ? 'error' : pct > 75 ? 'warning' : 'success'}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Paper>

      {/* Seat table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Seat</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {totalSeats === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No seats available. Please subscribe to a paid plan.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : seats.map((seat, i) => {
              const member = seat.assignedTo ? memberById(seat.assignedTo) : null;
              const isActive = seat.status === 'active' && !!seat.assignedTo;
              return (
                <TableRow key={seat.id || i} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">Seat {i + 1}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={isActive ? <ActiveIcon /> : <UnassignedIcon />}
                      label={isActive ? 'Active' : 'Unassigned'}
                      color={isActive ? 'success' : 'default'}
                      size="small"
                      variant={isActive ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    {member
                      ? `${member.firstName || ''} ${member.lastName || ''}`.trim() || member.displayName || '—'
                      : seat.assignedTo
                        ? <Typography variant="caption" color="text.secondary">{seat.assignedTo}</Typography>
                        : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {member?.email || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {isActive ? (
                      <Tooltip title="Revoke seat">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={actionLoading}
                            onClick={() => handleRevoke(seat.assignedTo)}
                          >
                            <RevokeIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Assign seat to a member">
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AssignIcon />}
                            disabled={actionLoading || unassignedMembers.length === 0}
                            onClick={() => { setAssignDialog({ seatId: seat.id, seatIndex: i }); setSelectedMember(''); }}
                          >
                            Assign
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Members without seats */}
      {unassignedMembers.length > 0 && totalSeats > usedSeats && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Members without a seat</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {unassignedMembers.map(m => (
              <Chip
                key={m.uid}
                label={`${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email}
                size="small"
                variant="outlined"
                onClick={() => {
                  setAssignDialog({ seatId: null, seatIndex: null });
                  setSelectedMember(m.uid);
                }}
                icon={<AssignIcon />}
              />
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary">
            Click a member chip to quickly assign them a seat.
          </Typography>
        </Paper>
      )}

      {/* Assign dialog */}
      <Dialog open={!!assignDialog} onClose={() => setAssignDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {assignDialog?.seatIndex != null ? `Assign Seat ${assignDialog.seatIndex + 1}` : 'Assign Seat'}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Select Member</InputLabel>
            <Select
              value={selectedMember}
              label="Select Member"
              onChange={e => setSelectedMember(e.target.value)}
            >
              {unassignedMembers.length === 0 ? (
                <MenuItem disabled>All members already have seats</MenuItem>
              ) : unassignedMembers.map(m => (
                <MenuItem key={m.uid} value={m.uid}>
                  {`${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email} — {m.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!selectedMember || actionLoading}
            onClick={handleAssign}
          >
            {actionLoading ? <CircularProgress size={18} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LicenseManagement;
